import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-only
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { IconTile } from '../icon-tile/index.js';
const variantTone = {
    success: 'pos',
    error: 'danger',
    warning: 'warn',
    info: 'info',
    loading: 'accent',
};
const toneBarBg = {
    neutral: 'bg-fg-subtle',
    accent: 'bg-accent',
    pos: 'bg-pos',
    warn: 'bg-warn',
    danger: 'bg-danger',
    info: 'bg-info',
};
/**
 * Default auto-dismiss durations in ms. The imperative queue manager
 * that actually fires dismissal is wave 2 — here the duration only
 * drives the visual `nb-toastbar` countdown.
 */
export const TOAST_DEFAULT_DURATIONS = {
    success: 2600,
    info: 3400,
    warning: 4200,
    error: 5200,
    loading: null,
};
/**
 * Toast — single toast primitive: icon tile · title · description · optional
 * action · close · bottom timer bar (`nb-toastbar` scaleX). Slide-in via
 * `nb-toastin`, RTL-mirrored to `nb-toastin-rtl`
 * (research/design-system.md Tier 3). Queue semantics (max 4, auto-dismiss,
 * `toast.*` imperative API) are wave 2 — this is the controlled primitive.
 */
export function Toast({ variant = 'info', title, description, action, onDismiss, dismissLabel, duration, className, ...props }) {
    const tone = variantTone[variant];
    const effectiveDuration = duration === undefined ? TOAST_DEFAULT_DURATIONS[variant] : duration;
    const icons = {
        success: _jsx(CheckCircle2, {}),
        error: _jsx(XCircle, {}),
        warning: _jsx(AlertTriangle, {}),
        info: _jsx(Info, {}),
        loading: _jsx(Loader2, { className: "animate-[nb-spin_.8s_linear_infinite]" }),
    };
    return (_jsxs("div", { role: variant === 'error' ? 'alert' : 'status', "data-variant": variant, className: cn('pointer-events-auto relative flex w-[360px] max-w-full items-start gap-3 overflow-hidden', 'rounded-lg border border-border bg-surface p-3.5 pb-4 shadow-menu', 'animate-[nb-toastin_.24s_cubic-bezier(.2,.7,.3,1)] rtl:animate-[nb-toastin-rtl_.24s_cubic-bezier(.2,.7,.3,1)]', className), ...props, children: [_jsx(IconTile, { size: "sm", tone: tone, icon: icons[variant] }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: "text-[13px] font-bold leading-snug text-fg", children: title }), description === undefined || description === null ? null : (_jsx("div", { className: "mt-0.5 text-body-sm text-fg-muted", children: description })), action === undefined ? null : (_jsx("button", { type: "button", onClick: action.onAction, className: cn('mt-1.5 text-[12.5px] font-bold text-accent hover:underline', 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'), children: action.label }))] }), onDismiss === undefined ? null : (_jsx("button", { type: "button", "aria-label": dismissLabel, onClick: onDismiss, className: cn('nb-ib -me-1 -mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-muted', 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'), children: _jsx(X, { className: "size-3.5", "aria-hidden": "true" }) })), effectiveDuration === null ? null : (_jsx("div", { "aria-hidden": "true", className: "absolute inset-x-0 bottom-0 h-[3px] bg-surface-3", children: _jsx("div", { "data-testid": "toast-timer-bar", className: cn('h-full w-full [transform-origin:0%_50%] rtl:[transform-origin:100%_50%]', 'animate-[nb-toastbar_var(--adm-duration,4200ms)_linear_forwards]', toneBarBg[tone]), style: { '--adm-duration': `${effectiveDuration}ms` } }) }))] }));
}
/**
 * ToastStack — fixed container at bottom / inline-end (RTL-mirrored via the
 * logical `end-4`) rendering a controlled list of toasts. Max-4 clamping,
 * FIFO overflow and auto-dismiss timers are the wave-2 queue manager's job;
 * callers own the array here.
 */
export function ToastStack({ toasts, onDismissToast, dismissLabel, label, className, ...props }) {
    return (_jsx("div", { role: "region", "aria-label": label, className: cn('pointer-events-none fixed bottom-4 end-4 z-[100] flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-2.5', className), ...props, children: toasts.map((toast) => (_jsx(Toast, { variant: toast.variant ?? 'info', title: toast.title, description: toast.description, action: toast.action, duration: toast.duration, dismissLabel: dismissLabel, onDismiss: () => onDismissToast(toast.id) }, toast.id))) }));
}
/** Alias — some docs refer to the stack container as the toast viewport. */
export const ToastViewport = ToastStack;
//# sourceMappingURL=Toast.js.map